import React, { useRef, useState } from 'react'

import { useBlockingConfirm } from '../hooks/useBlockingConfirm'

export type MatrixData = {
  temperatures: number[]
  weights: number[]
  values: number[][]
}

type MatrixTableProps = {
  title: string
  data: MatrixData
  onDataChange: (newData: MatrixData) => void
  rowLabel: string
  columnLabel: string
  valueLabel: string
  editable?: boolean
  precision?: number
}

export const MatrixTable: React.FC<MatrixTableProps> = ({
  title,
  data,
  onDataChange,
  rowLabel,
  columnLabel,
  valueLabel,
  editable = true,
  precision = 2
}) => {
  const [editingCell, setEditingCell] = useState<{row: number, col: number} | null>(null)
  const [pendingValue, setPendingValue] = useState<string>('')
  const confirmModal = useBlockingConfirm()
  // Guards against a duplicate ask() when Enter (handleKeyPress) and the
  // resulting focus change (onBlur, once the dialog's Cancel button steals
  // focus) both try to open the confirmation for the same edit.
  const confirmationPendingRef = useRef(false)

  const startEdit = (rowIndex: number, colIndex: number) => {
    if (!editable) return
    setEditingCell({row: rowIndex, col: colIndex})
    setPendingValue(data.values[rowIndex][colIndex].toString().replace('.', ','))
  }

  const confirmEdit = () => {
    if (!editingCell) return
    
    const numericValue = parseFloat(pendingValue.replace(',', '.'))
    if (isNaN(numericValue)) {
      alert('Please enter a valid number')
      return
    }

    const newValues = data.values.map((row, rIndex) =>
      row.map((value, cIndex) =>
        rIndex === editingCell.row && cIndex === editingCell.col
          ? numericValue
          : value
      )
    )

    onDataChange({
      ...data,
      values: newValues
    })

    setEditingCell(null)
    setPendingValue('')
  }

  const cancelEdit = () => {
    setEditingCell(null)
    setPendingValue('')
  }

  const requestEditConfirmation = async () => {
    if (!editingCell || confirmationPendingRef.current) return
    confirmationPendingRef.current = true
    const cell = editingCell
    const currentValue = data.values[cell.row][cell.col]
    try {
      const confirmed = await confirmModal.ask({
        message: (
          <>
            <strong>Confirm Change</strong>
            <br />
            Change SFR value at {data.weights[cell.row]}g / {data.temperatures[cell.col]}°C<br />
            from <strong>{currentValue.toFixed(precision).replace('.', ',')}</strong> to{' '}
            <strong>{pendingValue}</strong>?
          </>
        ),
        tone: 'default',
        confirmLabel: '✓ Confirm',
        cancelLabel: '✗ Cancel'
      })
      if (confirmed) {
        confirmEdit()
      } else {
        cancelEdit()
      }
    } finally {
      confirmationPendingRef.current = false
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void requestEditConfirmation()
    } else if (e.key === 'Escape') {
      cancelEdit()
    }
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0
        }}>
          {title}
        </h3>
        <div style={{
          fontSize: '12px',
          color: '#6b7280',
          textAlign: 'right'
        }}>
          <div>{valueLabel}</div>
          <div>{editable ? '🖊️ Click cells to edit' : '🔒 Read-only'}</div>
        </div>
      </div>

      <div style={{ 
        overflow: 'auto',
        maxHeight: '600px',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '13px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb',
                borderRight: '2px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9fafb',
                minWidth: '100px'
              }}>
                {rowLabel} \ {columnLabel}
              </th>
              {data.temperatures.map((temp, index) => (
                <th key={index} style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '2px solid #e5e7eb',
                  borderRight: index < data.temperatures.length - 1 ? '1px solid #e5e7eb' : 'none',
                  minWidth: '80px',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f9fafb'
                }}>
                  {temp}°C
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.weights.map((weight, rowIndex) => (
              <tr key={rowIndex} style={{
                backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f9fafb'
              }}>
                <td style={{
                  padding: '12px 16px',
                  fontWeight: '600',
                  color: '#374151',
                  borderRight: '2px solid #e5e7eb',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  position: 'sticky',
                  left: 0
                }}>
                  {weight}g
                </td>
                {data.values[rowIndex]?.map((value, colIndex) => (
                  <td key={colIndex} style={{
                    padding: '8px 12px',
                    textAlign: 'center',
                    borderRight: colIndex < data.temperatures.length - 1 ? '1px solid #e5e7eb' : 'none',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: editable ? 'pointer' : 'default',
                    backgroundColor: editingCell?.row === rowIndex && editingCell?.col === colIndex 
                      ? '#dbeafe' : 'transparent'
                  }}>
                    {editingCell?.row === rowIndex && editingCell?.col === colIndex ? (
                      <input
                        type="text"
                        value={pendingValue}
                        onChange={(e) => setPendingValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={() => void requestEditConfirmation()}
                        autoFocus
                        style={{
                          width: '60px',
                          border: '1px solid #3b82f6',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          textAlign: 'center',
                          fontSize: '13px'
                        }}
                      />
                    ) : (
                      <span
                        onClick={() => startEdit(rowIndex, colIndex)}
                        style={{
                          display: 'block',
                          padding: '4px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (editable) {
                            (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (editable) {
                            (e.target as HTMLElement).style.backgroundColor = 'transparent'
                          }
                        }}
                      >
                        {value.toFixed(precision).replace('.', ',')}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmModal.Confirm()}
    </div>
  )
}

export default MatrixTable